/**
 * @module features/users/components/UserAvatar
 * @description User avatar with deterministic offline SVG fallback and accessible labeling.
 */

import * as React from "react";
import { cn } from "@/lib/utils";

interface UserAvatarProps {
  name: string;
  avatarUrl?: string | null;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

const sizeClasses = {
  sm: "h-7 w-7 text-xs",
  md: "h-9 w-9 text-xs",
  lg: "h-11 w-11 text-sm",
  xl: "h-16 w-16 text-lg",
};

/**
 * Extract 2-letter uppercase initials from full name
 */
function getInitials(name: string): string {
  if (!name) return "US";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Generate a deterministic offline SVG data URI for reliable fallback
 */
function generateFallbackSvg(initials: string): string {
  return `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'><rect width='40' height='40' rx='8' fill='%232e2c29'/><text x='20' y='25' font-family='sans-serif' font-weight='600' font-size='15' text-anchor='middle' fill='%23e6f969'>${initials}</text></svg>`;
}

export function UserAvatar({
  name,
  avatarUrl,
  size = "md",
  className,
}: UserAvatarProps) {
  const [hasError, setHasError] = React.useState(false);
  const initials = getInitials(name);
  const fallbackSrc = React.useMemo(() => generateFallbackSvg(initials), [initials]);

  return (
    <div
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center rounded-full bg-muted overflow-hidden ring-1 ring-border/50 select-none",
        sizeClasses[size],
        className
      )}
      aria-label={`Avatar for ${name}`}
    >
      {avatarUrl && !hasError ? (
        <img
          src={avatarUrl}
          alt={name}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={() => setHasError(true)}
        />
      ) : (
        <img
          src={fallbackSrc}
          alt={name}
          className="h-full w-full object-cover"
        />
      )}
    </div>
  );
}
